import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  DbRoleWithActions,
  HandlersProgramResponse,
  HandlersProjectResponse,
  HandlersUserDetailResponse,
  useGetApiProgramsQuery,
  useGetApiProjectsQuery,
  useGetApiUsersRolesQuery,
} from '../../../../../app/athenaApiGen';
import {
  Role,
  RoleSection,
  UserRoleWithScopes,
  ZERO_GUID,
} from '../../../Types';
import RolePermissionsList from './RolePermissionsList';

interface RolesPanelProps {
  selectedUser: HandlersUserDetailResponse | null;
  setParentUserRolesWithScopes: (rolesWithScopes: UserRoleWithScopes) => void;
}

// Display-name overrides for role names whose UI presentation diverges
// from the backend value. `roleItem.roleName` remains the source of
// truth for lookups; this only affects what the user sees on screen.
const ROLE_DISPLAY_NAMES: Record<string, string> = {
  'Contracting Officer': 'COR',
};

const displayRoleName = (roleName: string) =>
  ROLE_DISPLAY_NAMES[roleName] ?? roleName;

export const RolesPanel: React.FC<RolesPanelProps> = ({
  selectedUser,
  setParentUserRolesWithScopes,
}) => {
  const theme = useTheme();

  const [categoryExpanded, setCategoryExpanded] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [expandedRoleInfo, setExpandedRoleInfo] = useState<
    Record<string, boolean>
  >({});
  const [shouldRenderRoleInfo, setShouldRenderRoleInfo] = useState<
    Record<string, boolean>
  >({});
  const [preShowRoleAnimation, setPreShowRoleAnimation] = useState<
    Record<string, boolean>
  >({});
  const [roles, setRoles] = useState<RoleSection[] | null>(null);
  const [userRolesWithScopes, setUserRolesWithScopes] =
    useState<UserRoleWithScopes>([]);

  const { data: { projects = [] } = {}, isFetching: projectsFetching } =
    useGetApiProjectsQuery({ page: 1, pageSize: 1000 });
  const { data: programs = [], isFetching: rolesFetching } =
    useGetApiProgramsQuery();
  const { data: rolesData = [], isFetching: programsFetching } =
    useGetApiUsersRolesQuery();

  // Build project and program options with "All" option.
  // The "All" project option carries a programId so that, when selected, the
  // payload can grant a program-level role (access to all current and future
  // projects in that program). We use the first available program as the
  // program context, mirroring the single-program assumption elsewhere in
  // this panel.
  const projectOptions = React.useMemo(() => {
    const all = {
      title: 'All',
      projectId: ZERO_GUID,
      programId: programs[0]?.programId,
    } as HandlersProjectResponse;
    if (projectsFetching) return [all];
    return [all, ...projects];
  }, [projects, projectsFetching, programs]);

  const programOptions = React.useMemo(() => {
    const all = {
      title: 'All',
      programId: ZERO_GUID,
    } as HandlersProgramResponse;
    if (rolesFetching) return [all];
    return [all, ...programs];
  }, [programs, programsFetching]);

  useEffect(() => {
    if (rolesFetching || !rolesData.length) return;
    // Transform flat roles array into grouped structure by roleType
    const groupedRoles: { [key: string]: RoleSection } = {};
    rolesData.forEach((role) => {
      const roleType = role.roleType || 'Other';
      if (!groupedRoles[roleType]) {
        groupedRoles[roleType] = {
          roleType,
          items: [],
        };
      }
      if (role.programId != undefined)
        groupedRoles[roleType].items.push(role as any as Role);
      else console.log('Undefined programId in role:', role);
    });

    // Convert to array and sort by roleType. Known categories follow a
    // curated display order; any unknown categories fall back to
    // alphabetical at the end.
    const ROLE_TYPE_ORDER = ['Platform Admin', 'Tyto Role', 'Federal Role'];
    const rolesArray = Object.values(groupedRoles).sort((a, b) => {
      const aIdx = ROLE_TYPE_ORDER.indexOf(a.roleType);
      const bIdx = ROLE_TYPE_ORDER.indexOf(b.roleType);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.roleType.localeCompare(b.roleType);
    });

    // Within each roleType section, order roles by permission count
    // descending so most-privileged roles surface first. Tiebreak
    // alphabetically for stability.
    rolesArray.forEach((section) => {
      section.items.sort((a, b) => {
        const diff = (b.actions?.length ?? 0) - (a.actions?.length ?? 0);
        if (diff !== 0) return diff;
        return a.roleName.localeCompare(b.roleName);
      });
    });

    setRoles(rolesArray);

    // Initialize categoryExpanded with all categories EXPANDED so the role
    // catalog (Federal Role, Platform Admin, Tyto Role, …) is visible on
    // first paint without requiring a user to be selected.
    const initialExpanded: Record<string, boolean> = {};
    rolesArray.forEach((section) => {
      initialExpanded[section.roleType] = true;
    });
    setCategoryExpanded(initialExpanded);
  }, [rolesData]);

  React.useEffect(() => {
    if (!rolesData.length || !programs.length || !projects.length || !roles)
      return;

    // If no user selected, keep categories EXPANDED (Federal Role,
    // Platform Admin, Tyto Role, …) so the role catalog is visible.
    if (!selectedUser) {
      const expandedAll: Record<string, boolean> = {};
      roles.forEach((section) => {
        expandedAll[section.roleType] = true;
      });
      setCategoryExpanded(expandedAll);
      setUserRolesWithScopes([]);
      setParentUserRolesWithScopes([]);
      return;
    }

    const userRolesWithScopes: UserRoleWithScopes = [];

    const add = (role: DbRoleWithActions) => {
      const newEntry = {
        role,
        projects: new Array<HandlersProjectResponse>(),
        programs: new Array<HandlersProgramResponse>(),
      };
      userRolesWithScopes.push(newEntry);
      return newEntry;
    };
    const userRoles = selectedUser.roles || [];

    userRoles.forEach((role) => {
      if (!role.roleName) return;
      const roleData = rolesData.find((r) => r.roleName === role.roleName);
      const userRoleEntry =
        userRolesWithScopes.find((ur) => ur.role.roleName === role.roleName) ||
        add(role);

      if (roleData) {
        if (isProjectRole(roleData)) {
          // Project roles
          if (role.projectId && role.projectId !== ZERO_GUID) {
            const project = projects.find(
              (p) => p.projectId === role.projectId,
            );
            if (project) {
              userRoleEntry.projects.push(project);
            }
          }
          // Program-level project grants (no projectId) are mapped to the
          // "All" option in post-processing below.
        } else {
          // Program roles
          if (role.programId && role.programId !== ZERO_GUID) {
            const program = programs.find(
              (p) => p.programId === role.programId,
            );
            if (program) {
              userRoleEntry.programs.push(program);
            }
          }
        }
      }
    });

    // A project-scoped role that the user holds but with no individual project
    // entries is a program-level grant — i.e. "All" projects in that program.
    // Represent it with the "All" sentinel so the Autocomplete shows "All".
    userRolesWithScopes.forEach((entry) => {
      const roleData = rolesData.find(
        (r) => r.roleName === entry.role.roleName,
      );
      if (roleData && isProjectRole(roleData) && entry.projects.length === 0) {
        const programGrant = userRoles.find(
          (r) => r.roleName === entry.role.roleName && r.programId,
        );
        entry.projects.push({
          title: 'All',
          projectId: ZERO_GUID,
          programId: programGrant?.programId,
        } as HandlersProjectResponse);
      }
    });

    // Always expand all categories on load
    const newCategoryExpanded: Record<string, boolean> = {};
    roles.forEach((section) => {
      newCategoryExpanded[section.roleType] = true;
    });

    setUserRolesWithScopes(userRolesWithScopes);
    setParentUserRolesWithScopes(userRolesWithScopes);
    setCategoryExpanded(newCategoryExpanded);
  }, [selectedUser, rolesData, programs, projects, roles]);

  // Handle role info animation timing
  useEffect(() => {
    Object.keys(expandedRoleInfo).forEach((roleName) => {
      if (expandedRoleInfo[roleName]) {
        setShouldRenderRoleInfo((prev) => ({
          ...prev,
          [roleName]: true,
        }));
        const preShowTimer = setTimeout(() => {
          setPreShowRoleAnimation((prev) => ({
            ...prev,
            [roleName]: true,
          }));
        }, 50);
        return () => clearTimeout(preShowTimer);
      } else {
        setPreShowRoleAnimation((prev) => ({
          ...prev,
          [roleName]: false,
        }));
        const timer = setTimeout(() => {
          setShouldRenderRoleInfo((prev) => ({
            ...prev,
            [roleName]: false,
          }));
        }, 300);
        return () => clearTimeout(timer);
      }
    });
  }, [expandedRoleInfo]);

  const isProjectRoleChecked = (role: DbRoleWithActions): boolean => {
    return userRolesWithScopes.some((ur) => ur.role.roleName === role.roleName);
  };

  // Handlers moved into component
  const handleCategoryToggle = (categoryName: string) => {
    if (!categoryExpanded) return;
    setCategoryExpanded((prev) => ({
      ...prev,
      [categoryName]: prev ? !prev[categoryName] : true,
    }));
  };

  const handleRoleToggle = (role: DbRoleWithActions) => {
    if (!selectedUser) return;

    if (userRolesWithScopes.some((ur) => ur.role.roleName === role.roleName)) {
      // Role is currently checked - remove it from userRolesWithScopes
      const newRolesWithScopes = userRolesWithScopes.filter(
        (ur) => ur.role.roleName !== role.roleName,
      );
      setUserRolesWithScopes(newRolesWithScopes);
      setParentUserRolesWithScopes(newRolesWithScopes);
    } else {
      // Role is currently unchecked - put it in the userRolesWithScopes with empty scopes
      const newEntry = {
        role,
        projects: new Array<HandlersProjectResponse>(),
        programs: isProjectRole(role)
          ? new Array<HandlersProgramResponse>()
          : programs[0]
            ? [programs[0]]
            : new Array<HandlersProgramResponse>(),
      };
      const newRolesWithScopes = [...userRolesWithScopes, newEntry];
      // Auto-expand role info when role is checked
      role.roleName &&
        setExpandedRoleInfo((prev) => ({
          ...prev,
          [role.roleName as string]: true,
        }));
      setUserRolesWithScopes(newRolesWithScopes);
      setParentUserRolesWithScopes(newRolesWithScopes);
    }
  };

  const handleRoleInfoToggle = (role: DbRoleWithActions) => {
    if (!role.roleName) return;
    const roleName = role.roleName;
    setExpandedRoleInfo((prev) => ({
      ...prev,
      [roleName]: !prev[roleName],
    }));
  };
  const isProjectRole = (role: DbRoleWithActions | Role | string): boolean => {
    if ((role as Role).roleName) {
      return (role as Role).scope?.toLowerCase() === 'project';
    }
    return (
      roles?.some((section) =>
        section.items.some(
          (r) => r.roleName === role && r.scope?.toLowerCase() === 'project',
        ),
      ) ?? false
    );
  };
  // Handle project scope selection from the multi-select Autocomplete.
  // "All" (ZERO_GUID) is mutually exclusive with individual projects:
  //   - selecting "All" clears any individually selected projects
  //   - selecting an individual project removes "All" if it was present
  const handleProjectScopeChange = (
    role: DbRoleWithActions,
    newValue: HandlersProjectResponse[],
  ) => {
    if (!role.roleName) return;

    const roleEntry = userRolesWithScopes.find(
      (ur) => ur.role.roleName === role.roleName,
    );

    if (!roleEntry) {
      console.warn('Role not found in userRolesWithScopes:', role.roleName);
      return;
    }

    const allWasSelected = roleEntry.projects.some(
      (p) => p.projectId === ZERO_GUID,
    );
    const allNowSelected = newValue.some((p) => p.projectId === ZERO_GUID);

    let next: HandlersProjectResponse[];
    if (allNowSelected && !allWasSelected) {
      // "All" was just selected → keep only "All"
      next = newValue.filter((p) => p.projectId === ZERO_GUID);
    } else if (allNowSelected && newValue.length > 1) {
      // An individual project was added while "All" was selected → drop "All"
      next = newValue.filter((p) => p.projectId !== ZERO_GUID);
    } else {
      next = newValue;
    }

    roleEntry.projects = next;
    setUserRolesWithScopes([...userRolesWithScopes]);
    setParentUserRolesWithScopes([...userRolesWithScopes]);
  };

  // Whether every real program is currently selected for a role — used to show
  // the "All" program option as checked.
  const isAllProgramsSelected = (role: DbRoleWithActions): boolean => {
    const roleEntry = userRolesWithScopes.find(
      (ur) => ur.role.roleName === role.roleName,
    );
    return (
      programs.length > 0 &&
      !!roleEntry &&
      roleEntry.programs.length === programs.length
    );
  };

  // Handle program scope selection from the multi-select Autocomplete.
  // Selecting "All" expands to every real program; selecting it again (while
  // everything is selected) clears the selection.
  const handleProgramScopeChange = (
    role: DbRoleWithActions,
    newValue: HandlersProgramResponse[],
  ) => {
    if (!role.roleName) return;

    const roleEntry = userRolesWithScopes.find(
      (ur) => ur.role.roleName === role.roleName,
    );

    if (!roleEntry) {
      console.warn('Role not found in userRolesWithScopes:', role.roleName);
      return;
    }

    const allWasSelected = isAllProgramsSelected(role);
    const allClicked = newValue.some((p) => p.programId === ZERO_GUID);

    let next: HandlersProgramResponse[];
    if (allClicked && !allWasSelected) {
      // "All" selected → select every real program
      next = [...programs];
    } else if (allClicked && allWasSelected) {
      // "All" toggled off while everything was selected → clear
      next = [];
    } else {
      next = newValue.filter((p) => p.programId !== ZERO_GUID);
    }

    roleEntry.programs = next;
    setUserRolesWithScopes([...userRolesWithScopes]);
    setParentUserRolesWithScopes([...userRolesWithScopes]);
  };

  function isRoleChecked(role: DbRoleWithActions): boolean | undefined {
    if (!role.roleName) return false;
    return userRolesWithScopes.some((ur) => ur.role.roleName === role.roleName);
  }

  // ... rest of the component implementation

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        mt: '2px',
      }}
    >
      {roles && categoryExpanded ? (
        roles.map((roleSection, sectionIdx) => (
          <Box
            key={sectionIdx}
            sx={{
              mb: 3,
            }}
          >
            {/* Category Header - static label; role rows below are always rendered */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 1,
                mb: 0,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  flex: 1,
                }}
              >
                {roleSection.roleType}
              </Typography>
            </Box>

            {/* Role Table (always visible) */}
            <TableContainer component={Paper} elevation={0}>
              <Table
                size="small"
                sx={{
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  '& .MuiTableCell-root': {
                    border: 'none',
                    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#444444' : '#B8B8B8'}`,
                  },
                }}
              >
                <colgroup>
                  <col style={{ width: '40px' }} />
                  <col style={{ width: 'auto' }} />
                  <col style={{ width: 'auto' }} />
                </colgroup>
                <TableBody>
                  {roleSection.items && roleSection.items.length > 0 ? (
                    roleSection.items.map((roleItem, itemIdx) => (
                      <React.Fragment key={itemIdx}>
                        <TableRow
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRoleInfoToggle(roleItem);
                          }}
                          sx={{
                            cursor: 'pointer',
                            ...(isRoleChecked(roleItem) && {
                              // Tyto Deep Blue tint (~20%) to highlight
                              // roles the selected user actually has.
                              backgroundColor: '#1F4D8333',
                            }),
                            '&:hover': {
                              backgroundColor: isRoleChecked(roleItem)
                                ? '#1F4D8345'
                                : 'action.hover',
                            },
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{ width: '40px', p: 1 }}
                          >
                            <Checkbox
                              checked={isRoleChecked(roleItem)}
                              onChange={() => handleRoleToggle(roleItem)}
                              onClick={(e) => e.stopPropagation()}
                              disabled={!selectedUser}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 0, pl: 0 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {expandedRoleInfo[roleItem.roleName] ? (
                                <KeyboardArrowDownIcon
                                  fontSize="small"
                                  sx={{ color: 'text.secondary', ml: -1 }}
                                />
                              ) : (
                                <KeyboardArrowRightIcon
                                  fontSize="small"
                                  sx={{ color: 'text.secondary', ml: -1 }}
                                />
                              )}
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {displayRoleName(roleItem.roleName)}
                                {roleItem.actions?.length !== undefined && (
                                  <Tooltip
                                    title={`${roleItem.actions.length} total permissions granted to this role`}
                                    placement="top"
                                    arrow
                                  >
                                    <Typography
                                      component="span"
                                      variant="body2"
                                      sx={{
                                        color: 'text.secondary',
                                        ml: 0.5,
                                      }}
                                    >
                                      ({roleItem.actions.length})
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          {/* Scope Selection */}
                          <TableCell sx={{ minWidth: 240 }}>
                            {isRoleChecked(roleItem) && (
                              <Box
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {isProjectRole(roleItem)
                                    ? 'Scope (Projects):'
                                    : 'Scope (Programs):'}
                                </Typography>
                                {isProjectRole(roleItem) ? (
                                  <Autocomplete
                                    multiple
                                    size="small"
                                    disableCloseOnSelect
                                    loading={projectsFetching}
                                    options={projectOptions}
                                    value={
                                      userRolesWithScopes.find(
                                        (ur) =>
                                          ur.role.roleName ===
                                          roleItem.roleName,
                                      )?.projects ?? []
                                    }
                                    isOptionEqualToValue={(option, val) =>
                                      option.projectId === val.projectId
                                    }
                                    getOptionLabel={(option) =>
                                      option.title ?? ''
                                    }
                                    onChange={(_, newValue) =>
                                      handleProjectScopeChange(
                                        roleItem,
                                        newValue,
                                      )
                                    }
                                    renderOption={(
                                      props,
                                      option,
                                      { selected },
                                    ) => {
                                      const { key, ...rest } = props as {
                                        key?: React.Key;
                                      } & React.HTMLAttributes<HTMLLIElement>;
                                      return (
                                        <li key={option.projectId} {...rest}>
                                          <Checkbox
                                            size="small"
                                            checked={selected}
                                            sx={{ mr: 1, p: 0.5 }}
                                          />
                                          <Typography
                                            variant="body2"
                                            sx={{ fontSize: '0.75rem' }}
                                          >
                                            {option.title}
                                          </Typography>
                                        </li>
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select projects"
                                      />
                                    )}
                                    sx={{ minWidth: 220, flex: 1 }}
                                  />
                                ) : (
                                  <Autocomplete
                                    multiple
                                    size="small"
                                    disableCloseOnSelect
                                    loading={rolesFetching}
                                    options={programOptions}
                                    value={
                                      userRolesWithScopes.find(
                                        (ur) =>
                                          ur.role.roleName ===
                                          roleItem.roleName,
                                      )?.programs ?? []
                                    }
                                    isOptionEqualToValue={(option, val) =>
                                      option.programId === val.programId
                                    }
                                    getOptionLabel={(option) =>
                                      option.title ?? ''
                                    }
                                    onChange={(_, newValue) =>
                                      handleProgramScopeChange(
                                        roleItem,
                                        newValue,
                                      )
                                    }
                                    renderOption={(
                                      props,
                                      option,
                                      { selected },
                                    ) => {
                                      const { key, ...rest } = props as {
                                        key?: React.Key;
                                      } & React.HTMLAttributes<HTMLLIElement>;
                                      const isAllOption =
                                        option.programId === ZERO_GUID;
                                      return (
                                        <li key={option.programId} {...rest}>
                                          <Checkbox
                                            size="small"
                                            checked={
                                              isAllOption
                                                ? isAllProgramsSelected(
                                                    roleItem,
                                                  )
                                                : selected
                                            }
                                            sx={{ mr: 1, p: 0.5 }}
                                          />
                                          <Typography
                                            variant="body2"
                                            sx={{ fontSize: '0.75rem' }}
                                          >
                                            {option.title}
                                          </Typography>
                                        </li>
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select programs"
                                      />
                                    )}
                                    sx={{ minWidth: 220, flex: 1 }}
                                  />
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                        {/* Expandable role info row */}
                        {shouldRenderRoleInfo[roleItem.roleName] && (
                          <TableRow
                            sx={{
                              transform: preShowRoleAnimation[roleItem.roleName]
                                ? 'translateY(0)'
                                : 'translateY(-10px)',
                              transition: 'transform 0.2s ease-out',
                            }}
                          >
                            <TableCell
                              style={{
                                paddingBottom: 0,
                                paddingTop: 0,
                                paddingLeft: 0,
                                paddingRight: 0,
                              }}
                              colSpan={3}
                            >
                              <Collapse
                                in={preShowRoleAnimation[roleItem.roleName]}
                                timeout={300}
                                unmountOnExit={false}
                              >
                                <Box
                                  sx={{
                                    p: 2,
                                    background:
                                      theme.palette.mode === 'light'
                                        ? 'transparent'
                                        : 'transparent',
                                    m: 1,
                                    position: 'relative',
                                  }}
                                >
                                  {/* Content Container with margin for close button */}
                                  <Box
                                    sx={{
                                      mb: 4, // Add margin bottom to make space for close button
                                    }}
                                  >
                                    <RolePermissionsList
                                      roleItem={roleItem}
                                      roles={roles}
                                    />
                                  </Box>

                                  {/* Close Button */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'flex-end',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      onClick={() =>
                                        handleRoleInfoToggle(roleItem)
                                      }
                                    >
                                      Close
                                    </Button>
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No roles available in this category
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))
      ) : (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Loading roles from API...
          </Typography>
        </Box>
      )}
    </Box>
  );
};
